// Tela da Diretoria na Visão Geral — #6495, projeto 24.
//
// Quase tudo reaproveita a aba CEO do Painel Executivo (mesmos ganchos, mesmo
// catálogo de KPIs, mesma régua de meta e farol): o número da tela inicial é o
// número da aba. O que é novo aqui:
//   - inadimplência pela regra do plano de bônus (decisão de 27/10/2026: a tela
//     inicial inteira usa essa regra, não a de 12 meses);
//   - a saúde de cada departamento resumida num farol (o pior dos seus KPIs).
//
// Lógica pura.

import { faixaDoAtingimento, type FaixaMeta } from '../dashboard/painelCeo.ts';
import { DEPARTAMENTOS, atingimentoKpi, type KpiDepartamento, type UnidadeKpi } from '../dashboard/kpisDepartamento.ts';
import { kpiAutomatico, kpisAutomaticosDe, melhoraDoKpi, type ContextoKpis, type DefinicaoKpiAuto } from '../dashboard/kpisAutomaticos.ts';
import { mesDaMeta, metaDoMes, type MetaMensal } from '../dashboard/metasMensais.ts';

// ── Inadimplência pela regra do plano de bônus ──
// Mora no catálogo de KPIs (vira indicador com meta na aba Metas); reexportada aqui.
export { inadimplenciaRegraBonus, janelaInadimplenciaBonus, type InadimplenciaBonus } from '../dashboard/kpisAutomaticos.ts';

// ── KPIs e saúde dos departamentos ──

export interface KpiAvaliado {
  chave: string;
  nome: string;
  unidade: UnidadeKpi;
  sentido: 'maior' | 'menor';
  atual: number | null;
  meta: number;
  /** Meta proporcional aos dias corridos no mês em andamento (igual à aba CEO). */
  metaAteAgora: number;
  atingimento: number | null;
  faixa: FaixaMeta;
  /** Positivo = melhor que o período anterior no mesmo ponto. */
  melhora: number | null;
  periodo: string;
  rotuloAnterior: string;
  /** Meta validada pelo Igor (true) ou sugerida pelo sistema (false). */
  validado: boolean;
  /**
   * De onde veio a meta: definida para o mês na aba Metas, a do KPI validado (ou
   * proposto) na aba CEO, ou a sugerida pelo sistema.
   */
  origemMeta: 'mes' | 'validada' | 'proposta' | 'sugerida';
  /** Mês da meta ('AAAA-MM') e, quando ela é repetida de antes, desde quando vale ('AAAA-MM-01'). */
  mesMeta: string;
  metaDesde: string | null;
  carregando: boolean;
}

/**
 * Avalia um KPI com a mesma régua do card da aba CEO (KpisDepartamentos.tsx):
 * meta e mês anterior proporcionais quando o mês ainda não acabou.
 *
 * Meta, nesta ordem: a do mês na aba Metas (`metas_mensais`), a do KPI salvo na
 * aba CEO, a sugerida pelo sistema.
 */
export function avaliarKpi(
  def: DefinicaoKpiAuto | undefined,
  ctx: ContextoKpis,
  salvo?: KpiDepartamento,
  metasMensais: readonly MetaMensal[] = [],
): KpiAvaliado | null {
  if (!def && !salvo) return null;
  const calculado = def ? def.calcular(ctx) : null;
  const atual = def ? (calculado?.atual ?? null) : (salvo!.valor_atual ?? null);
  const sentido = salvo?.sentido ?? def!.sentido;
  const chave = def?.id ?? `kpi:${salvo!.id}`;
  const mesMeta = mesDaMeta(calculado?.periodo, ctx.hoje);
  const doMes = metaDoMes(metasMensais, chave, mesMeta);
  const meta = doMes ? doMes.valor : salvo ? Number(salvo.meta) : calculado ? def!.metaSugerida(calculado, ctx) : 0;
  const origemMeta: KpiAvaliado['origemMeta'] = doMes ? 'mes' : salvo ? (salvo.status === 'validado' ? 'validada' : 'proposta') : 'sugerida';
  const fracao = calculado?.fracao ?? 1;
  const metaAteAgora = meta * fracao;
  const anteriorAteAgora = calculado?.anterior == null ? null : calculado.anterior * fracao;
  const temValor = def ? calculado != null : true;
  const atingimento = temValor ? atingimentoKpi({ meta: metaAteAgora, valor_atual: atual, sentido }) : null;
  return {
    chave,
    nome: salvo?.nome ?? def!.nome,
    unidade: salvo?.unidade ?? def!.unidade,
    sentido,
    atual,
    meta,
    metaAteAgora,
    atingimento,
    faixa: faixaDoAtingimento(atingimento),
    melhora: calculado ? melhoraDoKpi({ atual, anterior: anteriorAteAgora }, sentido) : null,
    periodo: calculado?.periodo ?? '',
    rotuloAnterior: calculado?.rotuloAnterior ?? '',
    validado: salvo?.status === 'validado',
    origemMeta,
    mesMeta,
    metaDesde: doMes?.herdada ? doMes.desde : null,
    carregando: !!def && calculado == null,
  };
}

const GRAVIDADE: Record<FaixaMeta, number> = { critico: 0, abaixo: 1, no_alvo: 2, acima: 3, sem_meta: 4 };

/** A faixa mais grave entre as que têm meta; sem nenhuma, 'sem_meta'. */
export function piorFaixa(faixas: FaixaMeta[]): FaixaMeta {
  return faixas.reduce<FaixaMeta>((pior, f) => (GRAVIDADE[f] < GRAVIDADE[pior] ? f : pior), 'sem_meta');
}

export interface SaudeDepartamento {
  id: string;
  rotulo: string;
  kpis: KpiAvaliado[];
  faixa: FaixaMeta;
  carregando: boolean;
}

/**
 * Os KPIs de cada departamento: os validados pelo Igor quando existem; senão, os
 * três sugeridos do catálogo (o mesmo que a aba CEO mostra antes da validação).
 */
export function saudeDosDepartamentos(ctx: ContextoKpis, salvos: KpiDepartamento[], metasMensais: readonly MetaMensal[] = []): SaudeDepartamento[] {
  return DEPARTAMENTOS.map(({ id, rotulo }) => {
    const validados = salvos.filter(k => k.departamento === id && k.status === 'validado').slice(0, 3);
    const kpis = (validados.length
      ? validados.map(k => avaliarKpi(kpiAutomatico(k.calculo_automatico), ctx, k, metasMensais))
      : kpisAutomaticosDe(id).slice(0, 3).map(def => avaliarKpi(def, ctx, undefined, metasMensais)))
      .filter((k): k is KpiAvaliado => k != null);
    const carregando = kpis.some(k => k.carregando);
    return { id, rotulo, kpis, faixa: carregando ? 'sem_meta' : piorFaixa(kpis.map(k => k.faixa)), carregando };
  });
}
